import React, { Fragment, useEffect, useRef, useState } from 'react'

import { Link } from 'react-router-dom'
import { Button, LoadingSpinner, VolunteerFilters, VolunteerFormModal, VolunteerProfileModal } from '../components'
import VolunteerTable from '../components/VolunteerTable'
import { AllLanguages, API_BASE_URL, DEFAULT_FILTERS, GenderOptions, IVPositionFilterOptions, LoadingSpinnerMeta, VolunteerFormModalMeta, VolunteerTableMeta, AVAILABILITY_OPTIONS, UPLOAD_DOCUMENT_TYPES } from '../data'
import { BaseFilterUpdate, Flag, Volunteer, VolunteersMetaProps, Training, Event } from '../data/types'
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '../icons'
import { generateCSID } from '../utils/volunteers'
import { EU_JAMAT_KHANA_OPTIONS } from '../data/meta'

const ITEMS_PER_PAGE = 10

type PaginationData = {
    current_page: number
    last_page: number
    per_page: number
    total: number
}

type Props = {
    meta: VolunteersMetaProps
}

export const Volunteers = (props: Props) => {
    const { meta } = props
    const [loading, setLoading] = useState(true)
    const [Volunteers, setVolunteers] = useState<Volunteer[]>([])
    const [selectedVolunteer, setSelectedVolunteer] = useState<Volunteer | null>(null);
    const [Trainigns, setTrainings] = useState<Training[]>([])
    const [Flags, setFlags] = useState<Flag[]>([])
    const [Events, setEvents] = useState<Event[]>([])
    const [error, setError] = useState<string | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
    const hasFetched = useRef(false)

    // Pagination state
    const [loadingPage, setLoadingPage] = useState<number>(1)
    const [pagination, setPagination] = useState<PaginationData>({
        current_page: 1,
        last_page: 1,
        per_page: ITEMS_PER_PAGE,
        total: 0
    })

    // Applied filters (what's actually sent to the server)
    const [appliedFilters, setAppliedFilters] = useState<BaseFilterUpdate>(DEFAULT_FILTERS)


    useEffect(() => {
        // Prevent duplicate fetches
        if (hasFetched.current) return
        hasFetched.current = true

        const fetchInitialData = async () => {
            // Fetch flags for filter dropdowns
            try {
                const response = await fetch(`${API_BASE_URL}/flag`, { credentials: 'include' })
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }
                const result = await response.json()
                setFlags(result)
            } catch (err) {
                console.error('Error fetching flags:', err)
            }

            try {
                const response = await fetch(`${API_BASE_URL}/training`, { credentials: 'include' })
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }
                const result = await response.json()
                setTrainings(result)
            } catch (err) {
                console.error('Error fetching trainings:', err)
            }

            // Fetch events for event assignment filter
            try {
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }
                const result = await response.json()
                let events = result.value || result
                // Handle double-nested arrays
                if (Array.isArray(events) && events.length > 0 && Array.isArray(events[0])) {
                    events = events[0]
                }
                setEvents(events)
            } catch (err) {
                console.error('Error fetching events:', err)
            }

            // Fetch first page of volunteers
            fetchVolunteers(1, appliedFilters)
        }

        fetchInitialData()
    }, [])

    const buildQueryString = (page: number, filters: BaseFilterUpdate) => {
        const params = new URLSearchParams()
        params.append('per_page', ITEMS_PER_PAGE.toString())
        params.append('page', page.toString())

        // Add all filter parameters
        if (filters.name) params.append('name', filters.name)
        if (filters.email) params.append('email', filters.email)
        if (filters.phone_number) params.append('phone_number', filters.phone_number)
        if (filters.id) params.append('id', filters.id)
        if (filters.country) params.append('country', filters.country)
        if (filters.country_of_birth) params.append('country_of_birth', filters.country_of_birth)
        if (filters.family_origin) params.append('family_origin', filters.family_origin)
        if (filters.jamat_khana) params.append('jamat_khana', filters.jamat_khana)
        if (filters.iv_position) params.append('iv_position', filters.iv_position)
        if (filters.age) params.append('age', filters.age.toString())
        if (filters.age_start) params.append('age_start', filters.age_start.toString())
        if (filters.age_end) params.append('age_end', filters.age_end.toString())

        // Boolean filters - only send if not null
        if (filters.ismaili !== null && filters.ismaili !== undefined) {
            params.append('ismaili', filters.ismaili.toString())
        }
        if (filters.registered_volunteer !== null && filters.registered_volunteer !== undefined) {
            params.append('registered_volunteer', filters.registered_volunteer.toString())
        }
        if (filters.profile_submitted !== null && filters.profile_submitted !== undefined) {
            params.append('profile_submitted', filters.profile_submitted.toString())
        }
        if (filters.approved_volunteer !== null && filters.approved_volunteer !== undefined) {
            params.append('approved_volunteer', filters.approved_volunteer.toString())
        }
        if (filters.eligible_for_duty !== null && filters.eligible_for_duty !== undefined) {
            params.append('eligible_for_duty', filters.eligible_for_duty.toString())
        }
        if (filters.multi_faith_family !== null && filters.multi_faith_family !== undefined) {
            params.append('multi_faith_family', filters.multi_faith_family.toString())
        }
        if (filters.has_support_needs !== null && filters.has_support_needs !== undefined) {
            params.append('has_support_needs', filters.has_support_needs.toString())
        }
        if (filters.volunteer_for_visit !== null && filters.volunteer_for_visit !== undefined) {
            params.append('volunteer_for_visit', filters.volunteer_for_visit.toString())
        }

        // String filters
        if (filters.iiuk_registeration) {
            params.append('iiuk_registeration', filters.iiuk_registeration)
        }

        // Array filters - send as JSON
        if (filters.genders && filters.genders.length > 0) {
            params.append('genders', JSON.stringify(filters.genders))
        }
        if (filters.service_record && filters.service_record.length > 0) {
            params.append('service_record', JSON.stringify(filters.service_record))
        }
        if (filters.clusters && filters.clusters.length > 0) {
            params.append('clusters', JSON.stringify(filters.clusters))
        }
        if (filters.languages && filters.languages.length > 0) {
            params.append('languages', JSON.stringify(filters.languages))
        }
        if (filters.tags && filters.tags.length > 0) {
            params.append('tags', JSON.stringify(filters.tags.map(t => t.id)))
        }

        if (filters.trainings && filters.trainings.length > 0) {
            params.append('trainings', JSON.stringify(filters.trainings.map(t => t.id)))
            // Add training filter type (defaults to 'at_least_one' if not specified)
            if (filters.training_filter_type) {
                params.append('training_filter_type', filters.training_filter_type)
            }
        }
        if (filters.availability && filters.availability.length > 0) {
            params.append('availability', JSON.stringify(filters.availability))
            if (filters.availability_filter_type) {
                params.append('availability_filter_type', filters.availability_filter_type)
            }
        }

        if (filters.documents && filters.documents.length > 0) {
            params.append('documents', JSON.stringify(filters.documents))
        }

        // Event assignment filter
        if (filters.event_assignment_type && filters.event_assignment_events && filters.event_assignment_events.length > 0) {
            params.append('event_assignment_type', filters.event_assignment_type)
            params.append('event_assignment_events', JSON.stringify(filters.event_assignment_events.map(e => e.id)))
        }

        // Core team assignment filter
        if (filters.core_team_assignment_type) {
            params.append('core_team_assignment_type', filters.core_team_assignment_type)
        }

        return params.toString()
    }

    const fetchVolunteers = async (page: number, filters: BaseFilterUpdate) => {
        setLoadingPage(page)
        setLoading(true)
        setError(null)

        try {
            const queryString = buildQueryString(page, filters)
            console.log("Query: ", queryString);
            const response = await fetch(`${API_BASE_URL}/volunteer?${queryString}`, { credentials: 'include' })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            const result = await response.json()
            console.log(result)

            // Handle paginated response structure
            const volunteersData = result.data || []

            // Normalize boolean fields
            const normalized = volunteersData.map((v: any) => ({
                ...v,
                eligible_for_duty: v.eligible_for_duty === 1,
                ismaili: v.ismaili === 1,
                registered_volunteer: v.registered_volunteer === 1,
                multi_faith_family: v.multi_faith_family === 1,
                volunteer_for_visit: v.volunteer_for_visit !== null && v.volunteer_for_visit !== undefined ? Number(v.volunteer_for_visit) : v.volunteer_for_visit
            }))

            setVolunteers(normalized)
            setPagination({
                current_page: result.current_page || 1,
                last_page: result.last_page || 1,
                per_page: result.per_page || ITEMS_PER_PAGE,
                total: result.total || 0
            })
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching volunteers')
        } finally {
            setLoading(false)
        }
    }




    const handleCreateVolunteer = async (volunteerData: any) => {

        const submissionData = {
            ...volunteerData,
            csid: generateCSID(),
            iiuk_registeration: false
        }

        const response = await fetch(`${API_BASE_URL}/volunteer`, {

            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(submissionData)
        })

        if (!response.ok) {
            console.log(response.json());
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        // Refetch current page after creating new volunteer
        if (result.model) {
            fetchVolunteers(pagination.current_page, appliedFilters)
        }
    }

    const handleFiltersChange = (update: BaseFilterUpdate) => {
        const newFilters = { ...appliedFilters, ...update }
        setAppliedFilters(newFilters)
        // Reset to page 1 when filters change
        fetchVolunteers(1, newFilters)
    }

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= pagination.last_page) {
            fetchVolunteers(page, appliedFilters)
        }
    }

    const handleProfileClick = (volunteer: Volunteer) => {
        setSelectedVolunteer(volunteer)
        setIsProfileModalOpen(true)
    }

    const handleProfileClickClose = () => {
        setSelectedVolunteer(null)
        setIsProfileModalOpen(false)
    }

    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className='mb-4'>
                        <Link
                            to="/admin"
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-2" />
                            Back to Admin Hub
                        </Link>
                    </div>
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                            {meta.description}
                        </p>
                        <div className="flex justify-center">
                            <Button onClick={() => setIsModalOpen(true)} size="lg">
                                <PlusIcon className="w-5 h-5" />
                                {meta.createButton}
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <div className="max-w-2xl mx-auto mb-6">
                            <div
                                className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1">
                        <VolunteerFilters
                            onChange={handleFiltersChange}
                            tagOptions={Flags}
                            languageOptions={AllLanguages}
                            serviceRecordOptions={IVPositionFilterOptions}
                            genderOptions={GenderOptions}
                            trainingOptions={Trainigns}
                            availabilityOptions={AVAILABILITY_OPTIONS as unknown as string[]}
                            clusterOptions={EU_JAMAT_KHANA_OPTIONS}
                            showRegisteredVolunteer={true}
                            eventOptions={Events}
                            documentOptions={UPLOAD_DOCUMENT_TYPES}
                        />

                        {loading ? (
                            <div className="flex justify-center py-12 border border-gray-200 dark:border-gray-700 rounded-lg">
                                <LoadingSpinner meta={LoadingSpinnerMeta} />
                            </div>
                        ) : Volunteers.length === 0 ? (
                            <div className="text-center text-gray-500 dark:text-gray-400 py-12 border border-gray-200 dark:border-gray-700 rounded-lg">
                                <p className="text-xl">{meta.noEventsMessage}</p>
                            </div>
                        ) : (
                            <VolunteerTable meta={VolunteerTableMeta} data={Volunteers} disablePagination={true} onProfileClick={handleProfileClick} />
                        )}

                        {/* Pagination Controls */}
                        {pagination.last_page > 1 && (
                            <div className="flex items-center justify-between mt-4">
                                <div className="flex items-center gap-2">
                                    <Button
                                        type="button"
                                        onClick={() => handlePageChange(1)}
                                        disabled={pagination.current_page <= 1}
                                        size="sm"
                                    >
                                        <ChevronLeftIcon /><ChevronLeftIcon className="w-5 h-5 -ms-[22px]" /> First
                                    </Button>

                                    <Button
                                        type="button"
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                        disabled={pagination.current_page <= 1}
                                        size="sm"
                                    >
                                        <ChevronLeftIcon /> Previous
                                    </Button>

                                    {(() => {
                                        const pageNumbers = []
                                        const maxPagesToShow = 5
                                        let startPage = Math.max(1, pagination.current_page - Math.floor(maxPagesToShow / 2))
                                        let endPage = Math.min(pagination.last_page, startPage + maxPagesToShow - 1)

                                        if (endPage - startPage < maxPagesToShow - 1) {
                                            startPage = Math.max(1, endPage - maxPagesToShow + 1)
                                        }

                                        if (startPage > 1) {
                                            pageNumbers.push(
                                                <Button
                                                    key={1}
                                                    type="button"
                                                    onClick={() => handlePageChange(1)}
                                                    size="sm"
                                                    variant="icon"
                                                >
                                                    1
                                                </Button>
                                            )
                                            if (startPage > 2) {
                                                pageNumbers.push(
                                                    <span key="ellipsis1" className="px-2 text-gray-500">...</span>
                                                )
                                            }
                                        }

                                        for (let i = startPage; i <= endPage; i++) {
                                            pageNumbers.push(
                                                <Button
                                                    key={i}
                                                    type="button"
                                                    onClick={() => handlePageChange(i)}
                                                    size="sm"
                                                    variant={i === loadingPage ? "solid" : "icon"}
                                                    color={i === loadingPage ? "primary" : "neutral"}
                                                >
                                                    {i}
                                                </Button>
                                            )
                                        }

                                        if (endPage < pagination.last_page) {
                                            if (endPage < pagination.last_page - 1) {
                                                pageNumbers.push(
                                                    <span key="ellipsis2" className="px-2 text-gray-500">...</span>
                                                )
                                            }
                                            pageNumbers.push(
                                                <Button
                                                    key={pagination.last_page}
                                                    type="button"
                                                    onClick={() => handlePageChange(pagination.last_page)}
                                                    size="sm"
                                                    variant="icon"
                                                >
                                                    {pagination.last_page}
                                                </Button>
                                            )
                                        }

                                        return pageNumbers
                                    })()}

                                    <Button
                                        type="button"
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                        disabled={pagination.current_page >= pagination.last_page}
                                        size="sm"
                                    >
                                        Next <ChevronRightIcon />
                                    </Button>

                                    <Button
                                        type="button"
                                        onClick={() => handlePageChange(pagination.last_page)}
                                        disabled={pagination.current_page >= pagination.last_page}
                                        size="sm"
                                    >
                                        Last <ChevronRightIcon /><ChevronRightIcon className="w-5 h-5 -ms-[22px]" />
                                    </Button>
                                </div>

                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    Page {pagination.current_page} of {pagination.last_page}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <VolunteerFormModal
                meta={VolunteerFormModalMeta}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleCreateVolunteer}
                adminSide={true}
            />

            <VolunteerProfileModal
                volunteerId={selectedVolunteer?.csid || null}
                isOpen={isProfileModalOpen}
                onClose={handleProfileClickClose}

            />

        </Fragment>
    )
}
